/**
 * Debounces a function
 * @template T
 * @param {T} func - The function to debounce
 * @param {number} delay - The delay in milliseconds
 * @param {boolean} [immediate=false] - Whether to execute the function immediately
 * @param {boolean} [debug=false] - Whether to log debug information about the debounce
 * @returns {(...args: Parameters<T>) => void} - The debounced function
 */

export const debounce = <T extends (...args: Parameters<T>) => void>(
  func: T,
  delay: number,
  immediate = false,
  debug = false
): ((...args: Parameters<T>) => void) => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined = undefined;
  const debugInstanceId =
    Date.now().toString(36) + Math.random().toString(36).substring(2, 7);

  return function debounced(...args: Parameters<T>) {
    const executeFunc = () => {
      if (debug) {
        console.log(
          `[debounce:${debugInstanceId}] Executing debounced function after ${delay}ms delay`
        );
      }
      func(...args);
    };

    if (debug) {
      if (timeoutId !== undefined) {
        console.log(
          `[debounce:${debugInstanceId}] Debounce reset - clearing previous timeout`
        );
      } else {
        console.log(
          `[debounce:${debugInstanceId}] Debounce triggered with ${delay}ms delay`
        );
      }
    }

    clearTimeout(timeoutId);

    if (immediate && timeoutId === undefined) {
      if (debug) {
        console.log(
          `[debounce:${debugInstanceId}] Immediate execution triggered`
        );
      }
      executeFunc();
    }

    timeoutId = setTimeout(executeFunc, delay);
  };
};
